import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import * as moment from "moment";
import { CoachService } from "./coach.service";
import { LssService } from "./lss.service";
import { ILssLoaded } from "app/models/lss";

@Injectable({
  providedIn: "root",
})
export class ScheduleService {
  private dailyScheduleSource = new BehaviorSubject<any>(null);
  dailySchedule$ = this.dailyScheduleSource.asObservable();
  constructor(
    private coachService: CoachService,
    private lssService: LssService
  ) {}

  getWeeklySchedule(
    startTime: string,
    endTime: string,
    oldRecord = false
  ): Observable<{
    allLsses: ILssLoaded[];
    weeklySchedule: any[];
    finalSchedule: any[];
    scheduleToDisplay: any[];
    dailySchedule: any[];
  }> {
    return combineLatest([
      this.coachService.getLssSchedule(startTime, endTime, ["1", "2", "3", "4", "5"], oldRecord),
      this.lssService.getAllLss({ deleted: false, archived: false }),
    ]).pipe(
      map(([schedules, lsses]) => {
        const weeklySchedule = this.processWeeklySchedule(schedules, lsses);
        const finalSchedule = this.getFinalSchedule(
          JSON.parse(JSON.stringify(weeklySchedule))
        );
        const dailySchedule = this.getDailySchedule(finalSchedule);
        if (dailySchedule) {
            this.updateDailySchedule(dailySchedule);
        }

        return {
          allLsses: lsses,
          weeklySchedule,
          finalSchedule,
          scheduleToDisplay: finalSchedule,
          dailySchedule,
        };
      })
    );
  }

  private processWeeklySchedule(schedules: any, lsses: ILssLoaded[]): any[] {
    const responseData = schedules?.["data"]?.data;
    if (!responseData || typeof responseData !== 'object') {
      return [];
    }
    let weeklySchedule = Object.entries(responseData);
    weeklySchedule.sort(
      (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
    );
    this.addDayOfWeek(weeklySchedule);
    return this.addAllLsses(weeklySchedule, lsses);
  }

  private addDayOfWeek(data: any[]): void {
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    if (data.length === 0) return;
    // Parse the date from the first item in the data array
    const startDate = new Date(data[0][0]);
    // Calculate the day offset
    // This converts Sunday (0) to 6, and other days shift by -1
    // Ensuring the week starts with Monday as 0
    const dayOffset = (startDate.getUTCDay() + 6) % 7;
  
    data.forEach((item, index) => {
      item["day"] = weekDays[(index + dayOffset) % 7];
    });
  }

  private addAllLsses(schedule: any[], lsses: ILssLoaded[]): any[] {
    return schedule.map((item) => {
      ["shiftOne", "shiftTwo", "shiftThree", "shiftFour", "shiftFive"].forEach((shift) => {
        if (item[1][shift]) {
          lsses.forEach((lss) => {
            const findLss = item[1][shift].find((i) => i.lssId == lss._id);
            if (!findLss) {
              item[1][shift].push({
                coaches: [],
                lssId: lss._id,
                firstName: lss.firstName,
                lastName: lss.lastName,
                status: "UNAVAILABLE",
              });
            }
          });
        }
      });
      return item;
    });
  }

  private getFinalSchedule(schedule: any[]): any[] {
    return schedule.map((item) => {
      ["shiftOne", "shiftTwo", "shiftThree", "shiftFour", "shiftFive"].forEach((shift) => {
        if (item[1][shift] && item[1][shift].length) {
          item[1][shift] = item[1][shift].filter(
            (i) => i.status != "UNAVAILABLE" && i.selected
          );
        }
      });
      return item;
    });
  }

  getDailySchedule(finalSchedule: any[]): any[] {
    const today = new Date().toISOString().split("T")[0];
    const currentDayData = finalSchedule.find(
      (dayData) => dayData[0] === today
    );
    return currentDayData ? currentDayData[1] : null;
  }

  updateDailySchedule(schedule: any) {
    this.dailyScheduleSource.next(schedule);
  }

  refreshDailySchedule(): Observable<any> {
    const startTime = moment().startOf("week").add(1, 'day').format("YYYY-MM-DD");
    const endTime = moment().endOf("week").add(1, 'day').format("YYYY-MM-DD");
    return this.getWeeklySchedule(startTime, endTime)
  }
}
